// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import { Card, CardActionArea, CardActions, CardMedia, Typography, IconButton, Tooltip, LinearProgress, Avatar, Popper, ClickAwayListener, Fade, Paper, MenuList, MenuItem } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ProjectPreview from 'Assets/projectPreview.png';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { Box, useTheme } from '@mui/system';
import { useSelector } from 'react-redux';
import { SetProjectIdAction } from 'Features/project/projectActions';
import { findFolderById, selectFolderList } from 'Features/folderList/folderListSelector';
import MoveProjectToFolderDialog from 'Components/MoveProjectToFolderDialog';
import OverflowTooltipText from 'Components/OverflowTooltipText';
import ProjectFolderBadge from 'Components/ProjectFolderBadge';
import ProjectProgressStepper from 'Components/ProjectProgressStepper';
import { resolveDeliverySchedule } from 'Utils/deliverySchedule';
import ProjectReferenceLine from 'Components/ProjectReferenceLine';
import { formatProjectDate, PROJECT_VALUE_UNSET } from 'Utils/projectDisplay';
import { isPrimaryPress } from 'Utils/pointerPress';
import { getPinStatusChip, getStatusDotColor } from 'Features/map/EsriDashboardMap/ProjectOverviewCard/statusChip';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import DeleteProjectDialog from 'Components/DeleteProjectDialog';
import { resolveProjectRoute } from '../resolveProjectRoute';
import { dataRequestBlocksDelete } from '../dataRequestBlocksDelete';
import { selectProjectInfo } from '../projectListSelector';
import { ReadThumbnailThunk } from '../projectListThunk';
import { DRAG_ACTIVATION_DISTANCE } from '../dragSensors';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

// No defaults for the optional props: defaultProps is deprecated for function components, and
// absence is the meaning (not inside a folder; not selected).
/* eslint-disable react/require-default-props */
type ProjectListItemProps = {
  // IProjectMapCoordinate rather than IProject: the dashboard renders the complete set and the
  // folder page the folder's projects, and every field this card reads is optional on the former
  // and present on the latter, so an IProject satisfies it too.
  project: IProjectMapCoordinate;
  isInsideFolder?: boolean;
  handleMoveProjectToFolderSuccess?: (project?: IProject, folderId?: string) => void;
  isSelected?: boolean;
};
/* eslint-enable react/require-default-props */

const ProjectListItem = ({ project, isInsideFolder, handleMoveProjectToFolderSuccess, isSelected }: ProjectListItemProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // One read of the project's derived state: how far along it is, what the chip calls it, what
  // it can be deleted under, and where clicking it goes.
  const projectInfo = useSelector((state) => selectProjectInfo(state, project));
  const {
    isInProgress, activeStep, cannotDelete, orderFinalized, expressAIReady, orderEstimate, orderStatus,
    imageryRequest, rasterRequest, orderEstimatedDeliveryDate, deliveryDate,
  } = projectInfo;
  const { resources } = useAppSelector(
    (state) => state.auth,
  );
  const projectDetailsTab = 'projectDetailsTab' in resources;
  const { esriViewer } = useFlags();
  const opsTrainer = 'opsTrainer' in resources;
  const folderList = useAppSelector(selectFolderList);
  const projectFolder = useMemo(
    () => (!isInsideFolder ? findFolderById(folderList, project.folderId) : undefined),
    [isInsideFolder, folderList, project.folderId],
  );

  // The rule itself lives in resolveProjectRoute, shared with the list row so the two
  // presentations of a project can never send the user to different places (AUT-6432).
  const projectUrl = resolveProjectRoute({
    projectId: project._id,
    orderFinalized: Boolean(orderFinalized),
    expressAIReady: Boolean(expressAIReady),
    esriViewer: Boolean(esriViewer),
    hasProjectDetailsTab: projectDetailsTab,
  });

  const thumbnail = !project.thumbnail || project.thumbnail.includes('undefined');

  // Raw S3 thumbnail URLs 403 against the private bucket, so the real image is resolved through
  // readThumbnail - lazily, once the card first scrolls into view, and only when this project's
  // thumbnail has not already been resolved this session: on mount it would request every
  // card's image before any is on screen, and again on every sort or filter change.
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true });
  useEffect(() => {
    if (inView && !thumbnail && !project.thumbnailResolved) {
      dispatch(ReadThumbnailThunk(project._id));
    }
  }, [inView, project.thumbnailResolved]);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const ref = useRef<HTMLElement | null>(null);

  // One node, two refs: the hover handlers already held this element, and the viewport observer
  // that defers the thumbnail needs it too.
  const setCardRef = useCallback((node: HTMLElement | null) => {
    ref.current = node;
    inViewRef(node);
  }, [inViewRef]);

  const [hovered, setHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showMoveProjectToFolderDialog, setShowMoveProjectToFolderDialog] = useState(false);

  const enter = () => {
    dispatch(SetProjectIdAction(project._id));
    setHovered(true);
  };
  const leave = () => {
    dispatch(SetProjectIdAction(null));
    setHovered(false);
  };

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.addEventListener('onmouseenter', enter);
      ref.current.addEventListener('onmouseleave', leave);
    }
    return () => {
      if (ref && ref.current) {
        ref.current.removeEventListener('onmouseenter', enter);
        ref.current.removeEventListener('onmouseleave', leave);
      }
    };
  }, [ref]);

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

  // Old projects can carry no creator at all, and a card must still draw for them.
  const initials = project.creator?.match(/\b\w/g) || [];

  const requestBlocksDelete = dataRequestBlocksDelete(imageryRequest, rasterRequest);

  // AUT-6432: the card's reference, date and status lines. The order behind them is the one
  // selectProjectInfo picked - the same pick the map rail and the pin card quote, so the three
  // never show different numbers for one project - and the reference is the project's own.
  const statusChip = getPinStatusChip({ orderStatus, isEstimate: orderEstimate });
  const statusDotColor = getStatusDotColor(theme, statusChip.kind);
  // A canceled order has no delivery coming, so the shared schedule blanks the date rather
  // than presenting a stale one as if it were still expected.
  const hasDeliverySchedule = resolveDeliverySchedule({
    orderStatus, deliveryDate: deliveryDate || undefined, deliveryEstimate: orderEstimatedDeliveryDate || undefined,
  }).state !== 'none';
  const dueDateText = hasDeliverySchedule ? formatProjectDate(deliveryDate) : PROJECT_VALUE_UNSET;

  const menuItemRef = useRef<HTMLButtonElement | null>(null);
  const handleMenuClose = () => setAnchorEl(null);

  // useDraggable hook is used to make the project item draggable.
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: project._id,
    data: { type: 'project' },
  });

  const draggableStyle = {
    transform: CSS.Translate.toString(transform),
  };

  const handleProjectClick = () => {
    navigate(projectUrl);
  };

  const dragStart = useRef<{x: number, y: number} | null>(null);

  // Handle pointer down to start tracking the drag: a main-button press only, so a middle or
  // right click neither arms a drag nor counts as a click on release.
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!isPrimaryPress(e)) return;
    dragStart.current = { x: e.clientX, y: e.clientY };
    if (listeners && listeners.onPointerDown) {
      listeners.onPointerDown(e);
    }
  };

  // Handle pointer up to determine if it was a click or a drag
  // if the pointer was moved more than the drag activation distance, it is considered a drag
  // Note: this was necessary due the large amount of events that were being triggered in the projects card (the dragging, the menu opening, the navigation, etc.)
  // this was the cleaner and most efficient way to handle it
  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isPrimaryPress(e)) return;
    const iconButton = document.getElementById(`menu-icon-${project._id}`);
    const moveToFolder = document.getElementById(`move-to-folder-${project._id}`);
    const deleteProject = document.getElementById(`delete-project-${project._id}`);

    if (moveToFolder && moveToFolder.contains(e.target as Node)) {
      handleMoveProjectToFolder();
      return;
    }

    if (deleteProject && deleteProject.contains(e.target as Node)) {
      handleDeleteProject();
      return;
    }

    if (iconButton && iconButton.contains(e.target as Node)) {
      setAnchorEl(menuItemRef.current);
      return;
    }

    if (!dragStart.current) return;
    const dx = Math.abs(e.clientX - dragStart.current.x);
    const dy = Math.abs(e.clientY - dragStart.current.y);
    if (dx < DRAG_ACTIVATION_DISTANCE && dy < DRAG_ACTIVATION_DISTANCE && !isMenuOpen) {
      handleProjectClick();
    }
    dragStart.current = null;
    if (listeners && listeners.onPointerUp) {
      listeners.onPointerUp(e);
    }
  };

  const handleMoveProjectToFolder = () => {
    window?.pendo?.track('Move To Folder');
    handleMenuClose();
    setShowMoveProjectToFolderDialog(true);
  };

  const handleDeleteProject = () => {
    window?.pendo?.track('Delete Project');
    handleMenuClose();
    setShowDeleteConfirmation(true);
  };

  return (
    <Box
      ref={setCardRef}
      // How the list finds this project to scroll to it, the same way the map rail's cards
      // are found. On the card itself rather than a wrapper, so the card stays the grid item.
      data-project-id={project._id}
      sx={[styles.cardWrapper, draggableStyle, Boolean(transform) && styles.cardDragging, isSelected && styles.cardSelected]}
      // The copy sits under the cursor for the whole drag. Left hoverable it would claim the
      // hovered-project state and hold it there, pinning the map to the dragged project.
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      {project.deleting && (
        <>
          <LinearProgress sx={styles.progress} />
          <Box sx={styles.loadingBackdrop} />
        </>
      )}
      <Card sx={styles.projectItem}>
        <Box
          title="View Project"
          ref={setNodeRef}
          {...attributes}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        >
          <CardActionArea>
            <CardMedia
              sx={styles.media}
              // The placeholder until the private-bucket URL is resolved - the raw URL is a 403.
              image={thumbnail || !project.thumbnailResolved ? ProjectPreview : project.thumbnail}
            >
              <Box className="backdrop" sx={{ ...styles.backdrop, ...(showDeleteConfirmation && styles.standardBackdrop), ...(isInProgress && styles.successBackdrop) }} />
              {projectFolder && <ProjectFolderBadge folderName={projectFolder.name} variant="overlay" />}
              <Box sx={styles.projectItemIcons} className="projectItemIcons">
                {hovered && (
                  <IconButton
                    id={`menu-icon-${project._id}`}
                    sx={styles.projectMenu}
                    size="small"
                    ref={menuItemRef}
                  >
                    <MoreHorizIcon sx={{ ...(styles.moreProjectMenuIcon), ...(isMenuOpen && styles.activeIcon) }} />
                  </IconButton>
                )}
                <Popper sx={styles.projectListItemPopper} open={isMenuOpen} anchorEl={anchorEl} transition>
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                      <Paper id={`menu-list-${project._id}`}>
                        <ClickAwayListener onClickAway={handleMenuClose}>
                          <MenuList sx={styles.menuList}>
                            <MenuItem
                              sx={styles.fontWeight}
                              id={`move-to-folder-${project._id}`}
                            >
                              Move To...
                            </MenuItem>
                            {!cannotDelete && !requestBlocksDelete && (
                              <MenuItem
                                sx={styles.fontWeight}
                                id={`delete-project-${project._id}`}
                              >
                                Delete
                              </MenuItem>
                            )}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Fade>
                  )}
                </Popper>
                {/* No creator, no avatar - an empty circle reads as a broken image rather than
                    as an absent name. */}
                {!opsTrainer && project.creator && (
                  <Avatar sx={styles.avatar} title={project.creator}>{initials.join('').toLowerCase()}</Avatar>
                )}
              </Box>
              {/* An in-progress project shows how far through the pipeline it is, over the
                  design's dark image treatment; the body below is the same on every card. */}
              {!showDeleteConfirmation && isInProgress && (
                <Box sx={styles.progressPanel}>
                  <ProjectProgressStepper activeStep={activeStep} />
                </Box>
              )}
              {!showDeleteConfirmation && !isInProgress && <Typography variant="h4" sx={styles.label} className="label">View Project</Typography>}
            </CardMedia>
          </CardActionArea>
          <CardActions sx={styles.cardActions}>
            <Box sx={styles.cardBody}>
              <OverflowTooltipText
                text={(opsTrainer ? project?.training?.nameEditable : project.name) || 'N/A'}
                sx={styles.titleText}
              />
              <ProjectReferenceLine projectId={project._id} referenceId={project.referenceId} createdAt={project.createdAt} sx={styles.refLine} />
              <Box sx={styles.metaRow}>
                <Typography sx={styles.metaText}>
                  <Box component="span" sx={styles.metaLabel}>Due Date:</Box>
                  {` ${dueDateText}`}
                </Typography>
                <Box sx={styles.statusRow}>
                  <Box sx={styles.statusDot(statusDotColor)} />
                  <Typography sx={styles.statusLabel}>{statusChip.label}</Typography>
                </Box>
              </Box>
            </Box>
          </CardActions>
        </Box>
      </Card>

      <DeleteProjectDialog
        showDialog={showDeleteConfirmation}
        setShowDialog={setShowDeleteConfirmation}
        projectId={project._id}
      />
      <MoveProjectToFolderDialog
        showDialog={showMoveProjectToFolderDialog}
        setShowDialog={() => setShowMoveProjectToFolderDialog(false)}
        projectId={project._id}
        projectFolderId={project.folderId}
        onSuccess={handleMoveProjectToFolderSuccess}
      />
    </Box>
  );
};

export default ProjectListItem;
